import { api } from "@Aspecto/backend/convex/_generated/api";
import { useMutation } from "convex/react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";

import { AppPageContent } from "@/components/layout/app-page";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const Route = createFileRoute("/_authenticated/app/onboarding-group-admin")({
  component: GroupAdminOnboardingPage,
});

function GroupAdminOnboardingPage() {
  const navigate = useNavigate();
  const completeOnboarding = useMutation(api.users.completeRlgAdminOnboarding);
  const skipOnboarding = useMutation(api.users.skipOnboarding);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const canSubmit = firstName.trim() && lastName.trim() && !isSubmitting;

  const handleComplete = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!canSubmit) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);
    try {
      await completeOnboarding({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
      });
      toast.success("Profile completed successfully!");
      navigate({ to: "/app" });
    } catch {
      toast.error("Failed to complete setup. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkip = async () => {
    setIsSubmitting(true);
    try {
      await skipOnboarding();
      navigate({ to: "/app" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AppPageContent className="flex min-h-svh flex-col justify-center">
      <Card className="w-full max-w-lg self-start shadow-lg">
        <CardHeader>
          <CardTitle className="text-2xl">Welcome to Aspecto</CardTitle>
          <CardDescription>
            You're joining as a <span className="font-medium text-foreground">Community Admin</span>
            . You'll be able to create and manage communities for your organisation.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleComplete} className="space-y-6">
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName">
                    First name <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="firstName"
                    placeholder="John"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    disabled={isSubmitting}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">
                    Last name <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="lastName"
                    placeholder="Doe"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    disabled={isSubmitting}
                  />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Your name will be visible to your team members.
              </p>
            </div>

            <div className="flex gap-4 pt-3">
              <Button
                type="button"
                variant="ghost"
                onClick={handleSkip}
                disabled={isSubmitting}
                className="flex-1"
              >
                Skip for now
              </Button>
              <Button type="submit" disabled={!canSubmit} className="flex-1">
                {isSubmitting ? "Saving..." : "Complete setup"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </AppPageContent>
  );
}
